import { Projectile } from '../../entities/Projectile.js';

const INITIAL_SIZE = 128;

/**
 * Object pool for player projectiles. Weapons call `fire()` instead of
 * `new Projectile()` so sustained auto-fire (the whole point of this game's
 * combat) doesn't allocate garbage every frame.
 *
 * Keeps every active projectile packed into `_all[0, _activeCount)` — a
 * dying one is swapped with the last active slot instead of just being
 * left in place, so `_activeCount` (and every scan below) only ever
 * reflects bullets that are actually alive right now. Without this, the
 * backing array only ever grows to match the *peak* concurrent count a run
 * ever reaches and then never shrinks back down — fire()'s free-slot scan,
 * update(), and every forEachActive() consumer (rendering, collision) all
 * keep paying that peak-sized cost for the rest of the run even once the
 * fight calms back down, which is what made runs feel progressively
 * laggier the longer they went.
 */
export class ProjectilePool {
  constructor(initialSize = INITIAL_SIZE) {
    this._all = Array.from({ length: initialSize }, () => new Projectile());
    this._activeCount = 0;
    // Piercing Gale (Rainbow Lance pet, data/pets.js): extra enemies every
    // player shot pierces through. 0 is the default single-hit behavior; set
    // by Game.js from the equipped pet.
    this.bonusHits = 0;
  }

  /** Grabs an inactive projectile (growing the pool if needed) and spawns it. */
  fire(config) {
    let proj;
    if (this._activeCount < this._all.length) {
      proj = this._all[this._activeCount];
    } else {
      proj = new Projectile();
      this._all.push(proj);
    }
    this._activeCount++;
    proj.spawn(config);
    if (this.bonusHits) proj.hitsRemaining += this.bonusHits;
    return proj;
  }

  update(dt) {
    for (let i = 0; i < this._activeCount; i++) {
      const p = this._all[i];
      p.update(dt);
      if (!p.active) {
        // Reclaim this slot: swap the last active projectile into it and
        // shrink the active window. Re-check the same index afterward
        // (i--) since it now holds a different, still-active projectile
        // that also needs updating this pass.
        this._activeCount--;
        this._all[i] = this._all[this._activeCount];
        this._all[this._activeCount] = p;
        i--;
      }
    }
  }

  /** Iterate only active projectiles — used by rendering and collision code. */
  forEachActive(fn) {
    for (let i = 0; i < this._activeCount; i++) {
      // Still defensively checked: a callback earlier in *this same* call
      // (or an external `.active = false` from collision code since the
      // last update()) can deactivate one of these before its own turn —
      // compaction only runs in update(), not mid-iteration here.
      const p = this._all[i];
      if (p.active) fn(p);
    }
  }

  get activeCount() {
    return this._activeCount;
  }
}
